import { createSlice } from '@reduxjs/toolkit';
import { apiCallBegan } from 'store/middleware/api/api';
import { depthInequality, ANY_OPTION } from 'src/enums/settings';
import { calculateDistanceBetweenCoordinates } from 'client/common/gis/calculate-distance-between-coordinates';
import { toast } from '@oliasoft-open-source/react-ui-library';
import i18n from 'i18next';
import translations from 'src/internationalisation/translation-map.json';

const offsetWells = createSlice({
  name: 'offsetWells',
  initialState: {
    isAdding: false,
    isFetching: false,
    list: [],
    selected: [],
    currentEstimate: null,
  },
  reducers: {
    offsetWellsRequested: (offsetWells) => {
      offsetWells.isFetching = true;
    },
    offsetWellsReceived: (offsetWells, action) => {
      const { list, selected, currentEstimate } = action.payload;
      offsetWells.isFetching = false;
      offsetWells.list = list;
      offsetWells.selected = selected;
      offsetWells.currentEstimate = currentEstimate;
    },
    offsetWellsRequestFailed: (offsetWells) => {
      offsetWells.isFetching = false;
    },
    selectOffsetWellsRequested: (offsetWells, action) => {
      offsetWells.isAdding = true;
      const { selected } = offsetWells;

      const listIndex = offsetWells.list.findIndex((item) => item.projectId === action.payload);
      offsetWells.list[listIndex].active = !offsetWells.list[listIndex].active;
      const selectedIndex = offsetWells.selected.findIndex((item) => item.projectId === action.payload);
      if (selectedIndex === -1) {
        offsetWells.selected.push(offsetWells.list[listIndex]);
      } else {
        delete selected[selectedIndex];
        offsetWells.selected = selected.filter((i) => i);
      }
    },
    selectOffsetWellsReceived: (offsetWells, action) => {
      offsetWells.isAdding = false;
      const { projectId, simulationResult, timeTracker } = action.payload;

      const selectedIndex = offsetWells.selected.findIndex((item) => item.projectId === projectId);

      if (selectedIndex !== -1) {
        offsetWells.selected[selectedIndex] = {
          ...offsetWells.selected[selectedIndex],
          simulationResult,
          timeTracker,
        };
      }
    },
    selectOffsetWellsRequestFailed: (offsetWells) => {
      offsetWells.isAdding = false;
    },
  },
});

export const {
  offsetWellsRequested,
  offsetWellsReceived,
  offsetWellsRequestFailed,
  selectOffsetWellsRequested,
  selectOffsetWellsReceived,
  selectOffsetWellsRequestFailed,
} = offsetWells.actions;
export default offsetWells.reducer;

/**
 * Get offset wells
 *
 * @param companyId
 * @param projectId
 */
export const getOffsetWells = (companyId, projectId) =>
  apiCallBegan({
    url: `/api/offset-wells/${companyId}/${projectId}`,
    method: 'GET',
    onStart: offsetWellsRequested.type,
    onSuccess: offsetWellsReceived.type,
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: i18n.t(translations.offsetWells_failedToGetOffsetWells),
        },
      });
      return {
        type: offsetWellsRequestFailed.type,
      };
    },
  });

/**
 * Select offset well
 *
 * @param projectId
 * @param offsetId
 * @param active
 */
export const selectOffsetWell = (projectId, offsetId, active) =>
  apiCallBegan({
    url: `/api/offset-wells/${projectId}`,
    method: active ? 'DELETE' : 'POST',
    data: { offsetId },
    onStart: () => ({
      type: selectOffsetWellsRequested.type,
      payload: offsetId,
    }),
    onSuccess: (response) => ({
      type: selectOffsetWellsReceived.type,
      payload: response,
    }),
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: i18n.t(translations.offsetWells_failedToSelectOffsetWell),
        },
      });
      return {
        type: selectOffsetWellsRequestFailed.type,
      };
    },
  });

/**
 * Filter offset wells list
 *
 * @param list
 * @param filter
 */
export const filterList = (list, filter, currentWellLocation) => {
  let result = list
    .filter((item) =>
      filter.waterDepthInequality === depthInequality.MORE_THAN_OR_EQUAL
        ? filter.waterDepth <= item.waterDepth
        : filter.waterDepth >= item.waterDepth,
    )
    .filter((item) =>
      filter.wellheadDepthInequality === depthInequality.MORE_THAN_OR_EQUAL
        ? filter.wellheadDepth <= item.wellheadDepth
        : filter.wellheadDepth >= item.wellheadDepth,
    )
    .filter((item) =>
      filter.casingStringsInequality === depthInequality.MORE_THAN_OR_EQUAL
        ? filter.casingStrings <= item.casingStrings
        : filter.casingStrings >= item.casingStrings,
    );
  if (filter.wellConfiguration !== ANY_OPTION) {
    result = result.filter((item) => item.wellConfiguration === filter.wellConfiguration);
  }
  if (filter.drillingUnit !== ANY_OPTION) {
    result = result.filter((item) => item.drillingUnit === filter.drillingUnit);
  }
  if (filter.distance) {
    result = result.filter(
      (item) => calculateDistanceBetweenCoordinates(item.location, currentWellLocation) <= filter.distance,
    );
  }

  return result;
};
